// src/contracts/feedback/types.ts
//
// 사내 피드백 게시판의 TypeScript 계약.
// mars/db/schema/feedback/{post,comment}.go 의 JSON 태그와 1:1 로 맞춘다.
//
// ── 이름이 어긋나 보이는 두 지점(의도된 것이므로 "고치지" 말 것) ──
//  1) 유형 필드의 JSON 이름은 `type` 이 아니라 **`category`** 다.
//     서버 스키마가 `Category string \`json:"category"\`` 로 확정되어 있고,
//     JSON 필드명은 백엔드 계약을 글자 그대로 따른다. UI 라벨과 컴포넌트
//     이름(FeedbackTypeChip 등)은 "유형"을 쓰지만 와이어 필드는 category 다.
//  2) 타임스탬프는 글·댓글 모두 `createdTimestamp` / `modifiedTimestamp` 다
//     (ContainerRental 관례). 반면 **정렬 키(sort)는 DB 컬럼명 기준**이라
//     `created_at_desc` 처럼 스네이크 케이스다 — 둘은 서로 다른 이름 공간이다.

/** 글 유형. 와이어에서는 `category` 필드에 담긴다. */
export type FeedbackType = "bug" | "improvement" | "other";

/** 처리 상태. 상태 변경은 작성자가 아니어도 누구나 가능하다. */
export type FeedbackStatus = "open" | "in_progress" | "done" | "declined";

/**
 * 정렬 키 — 서버 화이트리스트(DB 컬럼명 기준).
 * 서버는 여기에 `comment_count_desc` 도 갖고 있으나 총괄 확정 범위가
 * 4종(created/updated × asc/desc)이라 프론트는 4종만 노출한다.
 */
export type FeedbackSort =
  | "created_at_desc"
  | "created_at_asc"
  | "updated_at_desc"
  | "updated_at_asc";

export interface FeedbackCommentDto {
  id: string;
  postId: string;
  body: string;
  /** 인가 판정의 유일한 근거(JWT sub). 표시에 쓰지 않는다. */
  authorSub: string;
  /** 표시 전용 스냅샷. 클라이언트가 보낸 값이라 신뢰하지 않는다. */
  authorName: string;
  createdTimestamp: string; // RFC3339
  modifiedTimestamp: string; // RFC3339
}

export interface FeedbackPostDto {
  id: string;
  category: FeedbackType;
  title: string;
  body: string;
  status: FeedbackStatus;
  authorSub: string;
  authorName: string;
  /** 목록 행의 💬 수. 서버가 트랜잭션 안에서 COUNT(*) 재계산으로 유지한다. */
  commentCount: number;

  // 마지막 상태 변경 1건(변경 이력 아님). 미변경 글에는 키 자체가 없다.
  statusChangedAt?: string | null;
  statusChangedBySub?: string;
  statusChangedByName?: string;

  createdTimestamp: string;
  modifiedTimestamp: string;

  /**
   * 상세(GET /feedback/:id) 응답에만 존재하며, **댓글 0건이면 키 자체가 없다**
   * (`json:"comments,omitempty"`). 사용처는 반드시 `post.comments ?? []`.
   */
  comments?: FeedbackCommentDto[];
}

export interface FeedbackListParams {
  q?: string;
  /** 유형 필터. 빈 문자열은 "전체"이며 쿼리스트링에 실리지 않는다. */
  category?: FeedbackType | "";
  status?: FeedbackStatus | "";
  /** 0-base (서버 관례 — estimate 검색의 1-base 와 다르다). */
  page?: number;
  size?: number;
  sort?: FeedbackSort;
}

export interface FeedbackListResponse {
  items: FeedbackPostDto[];
  total: number;
  page: number;
  size: number;
}

/**
 * 글 작성/수정 payload.
 * `authorName` 은 표시용 스냅샷으로 프론트가 실어 보낸다
 * (액세스 토큰에 name 클레임이 없어 서버가 얻을 방법이 없음 — 백엔드 설계서 §3).
 * 수정(PUT) 시 서버는 category/title/body 만 반영하고 나머지는 무시한다.
 */
export interface FeedbackPostInput {
  category: FeedbackType;
  title: string;
  body: string;
  authorName?: string;
}

export interface FeedbackCommentInput {
  body: string;
  authorName?: string;
}

/** 목록 화면의 기본 파라미터. size 20 고정(총괄 확정). */
export const DEFAULT_FEEDBACK_PARAMS: FeedbackListParams = {
  q: "",
  category: "",
  status: "",
  page: 0,
  size: 20,
  sort: "created_at_desc",
};

/**
 * 표시명 폴백 — 서버가 빈 문자열을 줄 수 있다(작성 시 이름 미전달 등).
 * sub 전체를 화면에 노출하지 않는다(계정 식별자 노출 최소화).
 */
export const authorLabel = (p: {
  authorName?: string;
  authorSub?: string;
}): string => {
  const name = p.authorName?.trim();
  if (name) return name;
  const sub = p.authorSub ?? "";
  return sub ? `사용자 ${sub.slice(-6)}` : "이름 없음";
};

// TODO(feedback): 첨부파일/스크린샷 업로드 — 서버 스토리지 계약 확정 후
// TODO(feedback): 투표/추천(👍) — 목록 정렬 옵션에 인기순 추가 필요
// TODO(feedback): 알림(상태 변경·댓글) — 이메일/슬랙 채널 미정
// TODO(feedback): 대댓글(1단계 초과) — 스키마에 parentId 추가 필요
